import React, { useState, useMemo } from 'react';
import { useClientes, useCriarCliente, useAtualizarCliente, useRemoverCliente } from '../../hooks/useClientes';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useAuth } from '../../contexts/AuthContext';
import { Cliente } from '../../types';
import Button from '../../components/Common/Button';
import ConfirmModal from '../../components/Common/ConfirmModal';
import SearchBar from '../../components/Common/SearchBar';
import Input from '../../components/Common/Input';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { validateRequired, validateEmail, validatePhone, combineValidations } from '../../utils/validations';
import { aplicarMascaraTelefone, formatarTelefone } from '../../utils/formatters';

interface ClienteFormData {
  nome: string;
  telefone: string;
  email: string;
  endereco: string;
}

const Clientes: React.FC = () => {
  usePageTitle('Clientes');
  const { user } = useAuth();
  const { data: clientes, isLoading, refetch } = useClientes();
  const { mutate: criarCliente, isPending: criando } = useCriarCliente();
  const { mutate: atualizarCliente, isPending: atualizando } = useAtualizarCliente();
  const { mutate: removerCliente } = useRemoverCliente();

  const [termoBusca, setTermoBusca] = useState('');
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarModalNotas, setMostrarModalNotas] = useState(false);
  const [clienteEditando, setClienteEditando] = useState<Cliente | null>(null);
  const [clienteNotas, setClienteNotas] = useState<Cliente | null>(null);
  const [novaNota, setNovaNota] = useState('');
  const [formData, setFormData] = useState<ClienteFormData>({
    nome: '',
    telefone: '',
    email: '',
    endereco: '',
  });

  const [confirmarRemocao, setConfirmarRemocao] = useState<{
    isOpen: boolean;
    clienteId: number | null;
    clienteNome: string;
  }>({
    isOpen: false,
    clienteId: null,
    clienteNome: '',
  });

  const clientesFiltrados = useMemo(() => {
    if (!clientes) return [];
    if (!termoBusca.trim()) return clientes;

    const termo = termoBusca.toLowerCase();
    return clientes.filter(c =>
      c.nome.toLowerCase().includes(termo) ||
      c.telefone.includes(termoBusca) ||
      c.email?.toLowerCase().includes(termo) ||
      c.endereco?.toLowerCase().includes(termo)
    );
  }, [clientes, termoBusca]);


  const abrirModalNovo = () => {
    setClienteEditando(null);
    setFormData({
      nome: '',
      telefone: '',
      email: '',
      endereco: '',
    });
    setMostrarModal(true);
  };

  const abrirModalEditar = (cliente: Cliente) => {
    setClienteEditando(cliente);
    setFormData({
      nome: cliente.nome,
      telefone: cliente.telefone,
      email: cliente.email || '',
      endereco: cliente.endereco || '',
    });
    setMostrarModal(true);
  };

  const abrirModalNotas = async (cliente: Cliente) => {
    try {
      const response = await api.get(`/clientes/${cliente.id}`);
      setClienteNotas(response.data);
      setNovaNota('');
      setMostrarModalNotas(true);
    } catch (error: any) {
      toast.error('Erro ao carregar notas do cliente');
    }
  };

  const fecharModal = () => {
    setMostrarModal(false);
    setClienteEditando(null);
    setFormData({
      nome: '',
      telefone: '',
      email: '',
      endereco: '',
    });
  };

  const fecharModalNotas = () => {
    setMostrarModalNotas(false);
    setClienteNotas(null);
    setNovaNota('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.estabelecimentoId) {
      toast.error('Usuário sem estabelecimento vinculado');
      return;
    }

    const clienteData = {
      nome: formData.nome,
      telefone: formData.telefone.replace(/\D/g, ''),
      email: formData.email || undefined,
      endereco: formData.endereco || undefined,
    };

    if (clienteEditando) {
      atualizarCliente(
        {
          id: clienteEditando.id,
          ...clienteData,
        },
        {
          onSuccess: () => fecharModal(),
        }
      );
    } else {
      criarCliente(
        {
          ...clienteData,
          estabelecimentoId: user.estabelecimentoId,
        },
        {
          onSuccess: () => fecharModal(),
        }
      );
    }
  };

  const handleAdicionarNota = async () => {
    if (!clienteNotas || !novaNota.trim()) return;

    try {
      await api.post(`/clientes/${clienteNotas.id}/notas`, {
        nota: novaNota.trim(),
      });
      toast.success('Nota adicionada com sucesso!');

      // Recarrega os dados do cliente
      const response = await api.get(`/clientes/${clienteNotas.id}`);
      setClienteNotas(response.data);
      setNovaNota('');
      refetch();
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erro ao adicionar nota';
      toast.error(message);
    }
  };

  const abrirConfirmacaoRemocao = (id: number, nome: string) => {
    setConfirmarRemocao({
      isOpen: true,
      clienteId: id,
      clienteNome: nome,
    });
  };

  const fecharConfirmacaoRemocao = () => {
    setConfirmarRemocao({
      isOpen: false,
      clienteId: null,
      clienteNome: '',
    });
  };

  const handleConfirmarRemocao = () => {
    if (confirmarRemocao.clienteId) {
      removerCliente(confirmarRemocao.clienteId, {
        onSuccess: () => {
          fecharConfirmacaoRemocao();
        },
      });
    }
  };

  const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorFormatado = aplicarMascaraTelefone(e.target.value);
    setFormData({ ...formData, telefone: valorFormatado });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';

    try {
      // Remove o 'Z' e adiciona o offset de timezone para evitar problemas de conversão
      const date = new Date(dateString);

      // Verifica se a data é válida
      if (isNaN(date.getTime())) {
        return '-';
      }

      // Formata a data manualmente para garantir o formato correto
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch {
      return '-';
    }
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-3xl md:text-4xl font-display font-bold text-neutral-900">Clientes</h1>

        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <SearchBar
            value={termoBusca}
            onChange={setTermoBusca}
            placeholder="Buscar por nome, telefone, email ou endereço..."
            className="w-full sm:w-96"
          />

          <Button onClick={abrirModalNovo} variant="primary" size="md" className="whitespace-nowrap">
            Novo Cliente
          </Button>
        </div>
      </div>

      {termoBusca && (
        <p className="text-sm text-neutral-600 mb-4">
          {clientesFiltrados.length} cliente(s) encontrado(s)
        </p>
      )}

      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-neutral-600">Carregando clientes...</p>
        </div>
      ) : clientesFiltrados && clientesFiltrados.length > 0 ? (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md overflow-hidden">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Telefone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden sm:table-cell">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden sm:table-cell">
                  Endereço
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden sm:table-cell">
                  Data Cadastro
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clientesFiltrados.map((cliente) => (
                <tr key={cliente.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{cliente.nome}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatarTelefone(cliente.telefone)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                    <div className="text-sm text-gray-900">{cliente.email || '-'}</div>
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    <div className="text-sm text-gray-900">{cliente.endereco || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                    <div className="text-sm text-gray-900">{formatDate(cliente.dataCriacao)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => abrirModalNotas(cliente)}
                      className="text-success-600 hover:text-green-900 mr-4"
                      title="Gerenciar notas"
                    >
                      Notas
                    </button>
                    <button
                      onClick={() => abrirModalEditar(cliente)}
                      className="text-primary-600 hover:text-blue-900 mr-4"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => abrirConfirmacaoRemocao(cliente.id, cliente.nome)}
                      className="text-danger-600 hover:text-red-900"
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-8 text-center">
          <p className="text-neutral-600">Nenhum cliente cadastrado.</p>
        </div>
      )}

      {/* Modal de Confirmação de Remoção */}
      <ConfirmModal
        isOpen={confirmarRemocao.isOpen}
        title="Confirmar Remoção"
        message={`Tem certeza que deseja remover o cliente "${confirmarRemocao.clienteNome}"? Esta ação não pode ser desfeita.`}
        confirmText="Sim, remover"
        cancelText="Cancelar"
        onConfirm={handleConfirmarRemocao}
        onCancel={fecharConfirmacaoRemocao}
      />

      {/* Modal de Criar/Editar Cliente */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {clienteEditando ? 'Editar Cliente' : 'Novo Cliente'}
              </h3>
              <button
                onClick={fecharModal}
                className="text-gray-400 hover:text-neutral-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
                <Input
                  label="Nome *"
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome completo do cliente"
                  onValidate={validateRequired('Nome')}
                  validateOnBlur
                />

                <Input
                  label="Telefone *"
                  type="tel"
                  value={formData.telefone}
                  onChange={handleTelefoneChange}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                  onValidate={combineValidations(validateRequired('Telefone'), validatePhone)}
                  validateOnBlur
                  helperText="Formato: (XX) XXXXX-XXXX"
                />

                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="exemplo@email.com"
                  onValidate={validateEmail}
                  validateOnBlur
                />

                <Input
                  label="Endereço"
                  type="text"
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  placeholder="Rua, número, bairro, cidade"
                />
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-neutral-100">
                <Button
                  type="button"
                  onClick={fecharModal}
                  variant="secondary"
                  size="md"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={criando || atualizando}
                >
                  {criando || atualizando ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Notas */}
      {mostrarModalNotas && clienteNotas && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Notas de {clienteNotas.nome}
              </h3>
              <button
                onClick={fecharModalNotas}
                className="text-gray-400 hover:text-neutral-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <div className="space-y-4">
              {/* Lista de Notas */}
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md p-4">
                {clienteNotas.notas && clienteNotas.notas.length > 0 ? (
                  <ul className="space-y-2">
                    {clienteNotas.notas.map((nota, index) => (
                      <li key={index} className="p-3 bg-neutral-50 rounded border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">{nota}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-neutral-500 text-center py-4">Nenhuma nota cadastrada.</p>
                )}
              </div>

              {/* Adicionar Nova Nota */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adicionar Nova Nota
                </label>
                <textarea
                  value={novaNota}
                  onChange={(e) => setNovaNota(e.target.value)}
                  placeholder="Digite uma nova nota..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  onClick={fecharModalNotas}
                  variant="secondary"
                  size="md"
                >
                  Fechar
                </Button>
                <Button
                  type="button"
                  onClick={handleAdicionarNota}
                  variant="primary"
                  size="md"
                  disabled={!novaNota.trim()}
                >
                  Adicionar Nota
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientes;
